/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { useRef, useState } from "react";
import { EditorState, Modifier, convertToRaw, ContentState, convertFromHTML } from 'draft-js';
import { Editor } from "react-draft-wysiwyg";
import { TagPopover } from "../pages/marketing/create/steps/MessageEditorStep";
import draftToHtml from 'draftjs-to-html';
import debug from "../util/constants/Debug";

const HtmlButton = ({ onClick }) => (
  <div className="rdw-option-wrapper" title="Edit HTML" onClick={onClick}>
    <i className="icon bi bi-code-slash" />
  </div>
);

const TagButton = ({ onTag }) => {
  // tag popover state
  const [ popover, setPopover ] = useState(false);

  // handler
  const handleTag = (tag) => {
    onTag?.(tag);
    setPopover(false);
  };

  return (
    <>
      <div className="rdw-option-wrapper" id="editor-tag" title="Insert Tag" onClick={() => setPopover(true)}>
        <i className="icon bi bi-tag" />
      </div>
      <TagPopover isOpen={popover} toggle={() => setPopover(prev => !prev)} target="editor-tag" onTag={handleTag}/>
    </>
  )
};

const TextButton = ({ onClick }) => (
  <div className="rdw-option-wrapper" title="Edit Text" onClick={onClick}>
    <i className="icon bi bi-fonts" />
  </div>
);

const FileButton = ({ onClick }) => (
  <div className="rdw-option-wrapper" title="Upload File" onClick={onClick}>
    <i className="icon bi bi-file-earmark-arrow-up" />
  </div>
);

const TextEditor = ({
  state,
  updateState,
  showTag = false
}) => {
  const htmlRef = useRef(null);

  // styles
  const editorBorderStyle = {
    border: "1px solid #F1F1F1",
    borderRadius: "2px",
  };

  // html file upload
  const fileInputRef = useRef(null);

  const handleFileSelected = async (event) => {
    // validate 
    const selectedFile = event.target.files[0];
    if (!selectedFile) {
      debug.warn("Invalid file");
      return;
    }

    // read file in
    try {
      const fileReader = new FileReader();
      fileReader.onload = (e) => {
        const content = e.target.result;
        htmlRef.current.value = content;
        updateState({ htmlContent: content });
      }
      fileReader.readAsText(selectedFile);
    } catch (error) {
      debug.error("Error reading file: ", error);
      alert("Error reading file");
    }
  };

  // text/html mode toggle
  const toggleMode = () => {
    if (state.textMode) {
      // set to HTML edit mode
      const contentState = state.editorState.getCurrentContent();
      const html = draftToHtml(convertToRaw(contentState));
      updateState({ textMode: false, htmlContent: html });
    } else {
      // set to rich text mode
      const blocksFromHTML = convertFromHTML(state.htmlContent);
      const contentState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );
      updateState({ textMode: true, editorState: EditorState.createWithContent(contentState) });
    }
  };

  // handlers
  const handleKeyPress = (event) => {
    switch (event.key) {
      // add 2 spaces for tab
      case "Tab": {
        event.preventDefault();
        const start = htmlRef.current.selectionStart;
        const end = htmlRef.current.selectionEnd;
        const value = htmlRef.current.value;
        htmlRef.current.value = value.substring(0, start) + "  " + value.substring(end);
        htmlRef.current.selectionStart = htmlRef.current.selectionEnd = start + 2;
        break;
      }
      default: break;
    }
  };

  const handleInsertTag = (tag) => {
    const { editorState } = state;
    const contentState = Modifier.replaceText(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      tag,
      editorState.getCurrentInlineStyle(),
    );
    updateState({ editorState: EditorState.push(editorState, contentState, 'insert-characters') });
  };

  // editor buttons
  const editorButtons = [ <HtmlButton onClick={toggleMode} /> ];
  if (showTag) {
    editorButtons.push(<TagButton onTag={handleInsertTag} />);
  }

  return (
    <div className="w-100" style={{ height: "500px" }}>
      <div className={`fill-container ${state.textMode ? "d-block" : "d-none"}`}>
        <Editor
          editorState={state.editorState}
          editorStyle={{ ...editorBorderStyle, height: "425px" }}
          onEditorStateChange={(editorState) => updateState({ editorState })}
          toolbarCustomButtons={editorButtons}
        />
      </div>
      <div className={`d-flex flex-column fill-container ${state.textMode ? "d-none" : "d-block"}`}>
        <div className="d-flex p-2 mb-2" style={editorBorderStyle}>
          <TextButton onClick={toggleMode} />
          <FileButton onClick={() => fileInputRef.current.click()} />
        </div>
        <textarea
          className="w-100 flex-dynamic"
          value={state.htmlContent}
          onKeyDown={handleKeyPress}
          onChange={(e) => updateState({ htmlContent: e.target.value })}
          style={{
            ...editorBorderStyle,
            fontFamily: "monospace",
            tabSize: 2,
            display: "block",
            width: "100%",
            lineHeight: "1.4",
            padding: "10px",
            resize: "none",
            overflowX: "auto",
            overflowY: "auto",
          }}
        />
        <input ref={fileInputRef} type="file" accept=".txt,.html" style={{ display: 'none' }} onChange={handleFileSelected} />
      </div>
    </div>
  );
};

export default TextEditor;
